/**
 * Steel Bolts Tool (BIM: Steel > Fabricate Elements > Bolts)
 *
 * Two-phase placement:
 * 1) Pick the elements to be bolted together (structural members and/or
 *    plates). Every selected element is highlighted through appStore.select.
 * 2) Confirm with Enter, then click the spot where the bolt group starts.
 *    The bolt axis follows the clicked surface normal (falls back to the
 *    first member's transverse direction). Grid rows/columns/spacing and the
 *    bolt standard are driven by OptionsBar tool properties.
 */

import { BaseTool, ToolContext } from '../BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import {
    SteelFabricationManager,
    isSteelHostElement,
    resolveMemberFrame,
} from '../../core/SteelFabricationManager';
import i18n from '../../i18n';

type Phase = 'pick-elements' | 'place-group';

export class SteelBoltTool extends BaseTool {
    id = 'steel_bolts';
    name = 'Steel Bolts';
    cursor = 'crosshair';

    private phase: Phase = 'pick-elements';
    private pickedIds: string[] = [];

    activate(context: ToolContext): void {
        super.activate(context);
        this.notify(i18n.t('tools.notify.selectElementsToBoltThenPress'), 'info');
    }

    private isSelectable(elementId: string): boolean {
        if (!this.context) return false;
        const el = this.context.bimStore.project.elements.get(elementId);
        if (!el) return false;
        return isSteelHostElement(el) || el.category === 'Steel Plates';
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const hit = this.findElementHitAtPoint(event);
        if (!hit) return;
        if (!this.isSelectable(hit.element.id)) {
            this.notify(i18n.t('tools.notify.boltsMustConnectSteelFramingColumns'), 'warning');
            return;
        }

        if (this.phase === 'pick-elements') {
            if (!this.pickedIds.includes(hit.element.id)) {
                this.pickedIds.push(hit.element.id);
                this.context.appStore.select([...this.pickedIds]);
            }
            return;
        }

        // place-group phase: drop the bolt grid at the clicked surface point.
        this.placeBoltGroup(hit.point, hit.element, event);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context || this.phase !== 'place-group') return;
        const hit = this.findElementHitAtPoint(event);
        if (hit) {
            this.updatePreview(hit.point);
        } else {
            this.clearPreview();
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Enter' && this.phase === 'pick-elements') {
            if (this.pickedIds.length < 1) {
                this.notify(i18n.t('tools.notify.pickAtLeastOneElementFirst'), 'warning');
                return;
            }
            this.phase = 'place-group';
            this.notify(i18n.t('tools.notify.clickToPlaceTheBoltGroup'), 'info');
            event.preventDefault();
            return;
        }
        super.onKeyDown(event);
    }

    deactivate(): void {
        this.phase = 'pick-elements';
        this.pickedIds = [];
        this.context?.appStore.select([]);
        super.deactivate();
    }

    private axisAtClick(elementId: string, fallbackNormal: THREE.Vector3 | null): THREE.Vector3 {
        if (!this.context) return new THREE.Vector3(0, 0, 1);
        // Prefer the clicked member's transverse axis: bolts pass across it.
        const el = this.context.bimStore.project.elements.get(elementId);
        const frame = el ? resolveMemberFrame(el) : null;
        if (frame) {
            const memberDir = frame.end.clone().sub(frame.start).normalize();
            // Normal of clicked face, if it is not parallel to the member,
            // already gives the through direction; otherwise derive one.
            if (fallbackNormal && Math.abs(fallbackNormal.dot(memberDir)) < 0.85) {
                return fallbackNormal.clone();
            }
            const ref = Math.abs(memberDir.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
            return ref.cross(memberDir).normalize();
        }
        return fallbackNormal ?? new THREE.Vector3(0, 0, 1);
    }

    private placeBoltGroup(point: THREE.Vector3, element: { id: string }, event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const props = this.context.bimStore.toolProperties;

        // Surface normal from the raycast hit, when available.
        const hits = this.raycastSceneAtEvent(event);
        const raw = hits.find(h => this.resolveElementIdFromHit(h) === element.id);
        const faceNormal = raw?.face
            ? raw.face.normal.clone().applyQuaternion(raw.object.getWorldQuaternion(new THREE.Quaternion())).normalize()
            : null;

        const axis = this.axisAtClick(element.id, faceNormal);
        const bolt = SteelFabricationManager.createBoltGroup(
            point,
            axis,
            props.steelBoltRows ?? 2,
            props.steelBoltColumns ?? 2,
            Math.max((props.steelBoltSpacing ?? 80) / 1000, 0.02),
            this.pickedIds.slice(),
            {
                standard: props.steelBoltStandard ?? 'A325',
                diameterMm: props.steelBoltDiameter ?? 20,
                holeType: props.steelBoltHoleType ?? 'Standard',
            }
        );
        if (!bolt) return;

        this.context.bimStore.addElement(bolt);
        this.notify(i18n.t('tools.notify.boltsPlaced', { count: bolt.parameters['Quantity'], type: bolt.type }), 'success');
        this.clearPreview();
        // Stay in placement phase for repeated groups like BIM; Esc exits.
    }

    private updatePreview(center: THREE.Vector3): void {
        if (!this.context) return;
        const props = this.context.bimStore.toolProperties;
        const spacing = Math.max((props.steelBoltSpacing ?? 80) / 1000, 0.02);
        const rows = props.steelBoltRows ?? 2;
        const cols = props.steelBoltColumns ?? 2;
        this.context.appStore.setToolPreview({
            type: 'steel_bolts',
            center,
            rows,
            columns: cols,
            spacing,
        });
    }
}
